import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConsultationContext = createContext(null);

export function useConsultation() {
  const context = useContext(ConsultationContext);
  if (!context) {
    throw new Error('useConsultation must be used within ConsultationProvider');
  }
  return context;
}

const SECTIONS = [
  { key: 'general_info', label: 'General Information' },
  { key: 'chief_complaints', label: 'Chief Complaints' },
  { key: 'past_history', label: 'Past History' },
  { key: 'tumor_board', label: 'Tumor Board' },
  { key: 'clinical_examination', label: 'Clinical Examination' },
  { key: 'provisional_diagnosis', label: 'Provisional Diagnosis' },
  { key: 'final_diagnosis', label: 'Final Diagnosis' },
  { key: 'treatment_plan', label: 'Treatment Plan' },
  { key: 'prescription', label: 'Prescription' },
  { key: 'investigations', label: 'Investigations' },
  { key: 'admission_request', label: 'Admission Request' },
  { key: 'visit_notes', label: 'Visit Notes' },
  { key: 'review_date', label: 'Next Review Date' },
  { key: 'referral', label: 'Referral' },
  { key: 'visit_history', label: 'Visit History' }
];

export function ConsultationProvider({ children, consultationId, onBack }) {
  const { user } = useAuth();
  const [consultation, setConsultation] = useState(null);
  const [activeSection, setActiveSection] = useState('chief_complaints');
  const [saveStatus, setSaveStatus] = useState('saved');
  const [focusMode, setFocusMode] = useState(false);
  const [patients, setPatients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const saveTimeoutRef = useRef(null);

  // Load patients and templates
  const loadPatients = useCallback(async (search = '') => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/patients`, {
        params: { search },
        withCredentials: true
      });
      setPatients(data);
    } catch (error) {
      console.error('Failed to load patients:', error);
    }
  }, [user]);

  const loadTemplates = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/templates`, {
        withCredentials: true
      });
      setTemplates(data);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadTemplates();
      // Load specific consultation if ID provided
      if (consultationId) {
        loadConsultation(consultationId);
      }
    }
  }, [user, consultationId, loadTemplates]);

  // Create patient
  const createPatient = useCallback(async (patientData) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/patients`, patientData, {
        withCredentials: true
      });
      setPatients(prev => [data, ...prev]);
      return { success: true, patient: data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }, []);

  // Create consultation
  const createConsultation = useCallback(async (patientId, extraData = {}) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/consultations`,
        { patient_id: patientId, ...extraData },
        { withCredentials: true }
      );
      setConsultation(data);
      setActiveSection('chief_complaints');
      return { success: true, consultation: data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }, []);

  // Load consultation
  const loadConsultation = useCallback(async (consultationId) => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/consultations/${consultationId}`,
        { withCredentials: true }
      );
      setConsultation(data);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }, []);

  // Save draft with debounce
  // pendingSavesRef holds the latest pending content *per section* so fast
  // edits across different sections don't overwrite each other.
  const pendingSavesRef = useRef({}); // { [section]: content }

  const flushPendingSave = useCallback(async () => {
    const pending = pendingSavesRef.current;
    if (!pending || Object.keys(pending).length === 0 || !consultation) return false;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    // Snapshot and clear the queue before firing so concurrent edits don't race.
    const toFlush = pending;
    pendingSavesRef.current = {};
    setSaveStatus('saving');
    let anyFailed = false;
    for (const [section, content] of Object.entries(toFlush)) {
      try {
        await axios.post(
          `${API_URL}/api/consultations/save-draft`,
          { encounter_id: consultation.id, section, content },
          { withCredentials: true }
        );
        setConsultation(prev => {
          if (!prev) return prev;
          const sectionUpdate = {
            ...prev.sections[section],
            content,
            status: content ? 'completed' : 'not_started'
          };
          if (section === 'prescription' && Array.isArray(content)) sectionUpdate.items = content;
          else if (section === 'investigations' && Array.isArray(content)) sectionUpdate.items = content;
          else if (section === 'review_date') sectionUpdate.date = content;
          else if (section === 'admission_request') sectionUpdate.data = content;
          else if (section === 'referral') sectionUpdate.data = content;
          return { ...prev, sections: { ...prev.sections, [section]: sectionUpdate } };
        });
      } catch (error) {
        console.error(`Failed to save draft [${section}]:`, error);
        anyFailed = true;
        // Re-queue so a later retry can pick it up
        if (!pendingSavesRef.current[section]) {
          pendingSavesRef.current[section] = content;
        }
        // Make failures visible — a silent save loss is how data vanishes
        // between "I saved" and "I reopened". Once a toast shows, the doctor
        // knows to fix connectivity / re-login and retry before closing.
        try {
          const isAuthIssue = error?.response?.status === 401 || error?.response?.status === 403;
          const msg = isAuthIssue
            ? 'Session expired — please log in again to save changes.'
            : (error?.response?.data?.detail || error?.message || 'Network error');
          toast.error(`Could not save ${section.replace(/_/g,' ')}`, { description: msg });
        } catch (_) { /* sonner not ready */ }
      }
    }
    setSaveStatus(anyFailed ? 'error' : 'saved');
    return !anyFailed;
  }, [consultation]);

  const saveDraft = useCallback(async (section, content) => {
    if (!consultation) return;
    // Record the latest pending change for this section (per-section queue)
    pendingSavesRef.current = { ...pendingSavesRef.current, [section]: content };

    // Tuned to 5s — short enough that a doctor can step away within seconds
    // and still have everything saved, but long enough that every keystroke
    // doesn't generate a save-storm. Manual Save button + saveNow() on
    // navigation/print/complete still flush instantly.
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      flushPendingSave();
    }, 5000);
  }, [consultation, flushPendingSave]);

  // Immediate save: flush any pending debounced change
  const saveNow = useCallback(async () => {
    await flushPendingSave();
  }, [flushPendingSave]);

  // Ref that always points at the latest flushPendingSave so the unmount
  // cleanup below can call it without being recreated (and without causing
  // re-renders of the provider).
  const flushRef = useRef(flushPendingSave);
  useEffect(() => { flushRef.current = flushPendingSave; }, [flushPendingSave]);

  // Flush any pending save when the provider unmounts (e.g., user navigates
  // back to the dashboard before the 500ms debounce timer fires). This makes
  // data persistence robust against fast navigation.
  useEffect(() => {
    return () => {
      try { flushRef.current?.(); } catch (_) { /* best-effort */ }
    };
  }, []);

  // Also flush on page hide / tab close so unsaved changes aren't lost.
  useEffect(() => {
    const handler = () => { try { flushRef.current?.(); } catch (_) {} };
    window.addEventListener('beforeunload', handler);
    window.addEventListener('pagehide', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
      window.removeEventListener('pagehide', handler);
    };
  }, []);

  // Complete consultation (also posts prescriptions to pharmacy)
  const completeConsultation = useCallback(async () => {
    if (!consultation) return { success: false };
    // Flush any pending debounced save so the backend has the latest data
    // before it assembles the pharmacy payload.
    await flushPendingSave();
    try {
      const { data } = await axios.post(
        `${API_URL}/api/consultations/${consultation.id}/complete`,
        {},
        { withCredentials: true }
      );
      setConsultation(prev => ({
        ...prev,
        status: 'completed',
        pharmacy_posted: !!data?.pharmacy_posted,
        pharmacy_order_number: data?.pharmacy_order_number || '',
        pharmacy_post_error: data?.pharmacy_error || '',
        investigations_posted: !!data?.investigations_posted,
        investigations_order_number: data?.investigations_order_number || '',
        investigations_post_error: data?.investigations_error || '',
      }));
      // Schedule a follow-up appointment automatically if the doctor
      // filled in the "Follow-up in N days" picker. The appointment is
      // created with `status=awaiting_payment` so it shows in reception
      // for fee collection but stays out of the doctor queue until paid.
      // Failure is silent — completion itself succeeded.
      try {
        const reviewVal = consultation?.sections?.review_date;
        const reviewStr = typeof reviewVal === 'string'
          ? reviewVal
          : (reviewVal?.content || '');
        const datePart = (reviewStr || '').split(' ')[0];
        const targetDate = datePart ? new Date(datePart) : null;
        if (targetDate && !isNaN(targetDate.getTime())) {
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const tgt = new Date(targetDate); tgt.setHours(0, 0, 0, 0);
          const inDays = Math.round((tgt - today) / 86400000);
          if (inDays >= 1 && inDays <= 365 && consultation.patient_id) {
            await axios.post(
              `${API_URL}/api/clinic-ops/auto-appointments/follow-up`,
              {
                patient_id: consultation.patient_id,
                in_days: inDays,
                doctor_id: consultation.doctor_id || '',
                origin_consultation_id: consultation.id,
              },
              { withCredentials: true },
            );
          }
        }
      } catch (e) {
        console.warn('Auto-followup appointment creation failed:', e);
      }
      return {
        success: true,
        pharmacyPosted: !!data?.pharmacy_posted,
        pharmacyOrderNumber: data?.pharmacy_order_number || '',
        pharmacyError: data?.pharmacy_error || '',
        investigationsPosted: !!data?.investigations_posted,
        investigationsOrderNumber: data?.investigations_order_number || '',
        investigationsError: data?.investigations_error || '',
      };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }, [consultation, flushPendingSave]);

  // Retry pharmacy posting (if it failed on complete)
  const retryPharmacyPosting = useCallback(async () => {
    if (!consultation) return { success: false };
    try {
      const { data } = await axios.post(
        `${API_URL}/api/consultations/${consultation.id}/retry-pharmacy`,
        {},
        { withCredentials: true }
      );
      setConsultation(prev => ({
        ...prev,
        pharmacy_posted: !!data?.pharmacy_posted,
        pharmacy_order_number: data?.pharmacy_order_number || '',
        pharmacy_post_error: data?.pharmacy_error || '',
      }));
      return {
        success: true,
        pharmacyPosted: !!data?.pharmacy_posted,
        pharmacyOrderNumber: data?.pharmacy_order_number || '',
        pharmacyError: data?.pharmacy_error || '',
      };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }, [consultation]);

  // Get AI suggestions — now patient-context-aware. The consultation
  // doc carries patient demographics + current vitals (from triage
  // and/or the consultation's own vitals section) and the current
  // prescription items (so the LLM can flag interactions with what
  // the doctor has already added). All extra fields are optional;
  // the backend tolerates missing context and falls back gracefully.
  const getSuggestions = useCallback(async (text, type, context = '') => {
    try {
      const c = consultation || {};
      const sections = c.sections || {};
      const vitals = sections.vitals || c.vitals || null;
      const currentMeds = (sections.prescription?.items || c.prescription || [])
        .map(m => m?.name).filter(Boolean);
      const payload = {
        text,
        suggestion_type: type,
        context,
        patient_id: c.patient_id || null,
        patient_age: c.patient_age || c.age || null,
        patient_gender: c.patient_gender || c.gender || null,
        patient_weight: vitals?.weight ?? null,
        current_medications: currentMeds.length ? currentMeds : null,
        vitals: vitals && Object.keys(vitals).length ? vitals : null,
      };
      const { data } = await axios.post(
        `${API_URL}/api/suggestions`,
        payload,
        { withCredentials: true }
      );
      return { success: true, suggestions: data.suggestions };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || error.message };
    }
  }, [consultation]);

  // Calculate completion percentage
  const getCompletionPercentage = useCallback(() => {
    if (!consultation?.sections) return 0;
    const sections = Object.values(consultation.sections);
    const completed = sections.filter(s => s.status === 'completed').length;
    return Math.round((completed / sections.length) * 100);
  }, [consultation]);

  // Apply template
  const applyTemplate = useCallback((template) => {
    if (!consultation || !template) return;
    
    const content = template.content;
    Object.keys(content).forEach(section => {
      if (consultation.sections[section]) {
        saveDraft(section, content[section]);
      }
    });
  }, [consultation, saveDraft]);

  const value = {
    consultation,
    setConsultation,
    activeSection,
    setActiveSection,
    saveStatus,
    focusMode,
    setFocusMode,
    patients,
    templates,
    SECTIONS,
    loadPatients,
    createPatient,
    createConsultation,
    loadConsultation,
    saveDraft,
    saveNow,
    completeConsultation,
    retryPharmacyPosting,
    getSuggestions,
    getCompletionPercentage,
    applyTemplate,
    onBack
  };

  return (
    <ConsultationContext.Provider value={value}>
      {children}
    </ConsultationContext.Provider>
  );
}
